import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaGlobe } from 'react-icons/fa';

const Contact = ({ contactInfo, address }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitSuccess(false), 3000);
    }, 1500);
  };

  const formattedAddress = address
    ? `${address.street}, ${address.city}, ${address.state}, ${address.country}`
    : "Sector 12, Block H, Pratap Bihar, Ghaziabad, Uttar Pradesh, India";

  const contactCards = [
    {
      icon: <FaPhoneAlt className="text-xl" />,
      title: "Call Us",
      info: contactInfo?.phoneNumbers || ["+91 8080779602", "+91 8368599479"],
      action: `tel:${contactInfo?.phoneNumbers?.[0] || "+918080779602"}`
    },
    {
      icon: <FaEnvelope className="text-xl" />,
      title: "Email Us",
      info: ["info@vaishnavieducare.com"],
      action: "mailto:info@vaishnavieducare.com"
    },
    {
      icon: <FaMapMarkerAlt className="text-xl" />,
      title: "Visit Us",
      info: [
        address?.street || "Sector 12, Block H, Pratap Bihar",
        `${address?.city || "Ghaziabad"}, ${address?.state || "Uttar Pradesh"}`
      ],
      action: `https://maps.google.com/?q=${encodeURIComponent(formattedAddress)}`
    },
    {
      icon: contactInfo?.alternateWebsite ? <FaGlobe className="text-xl" /> : <FaWhatsapp className="text-xl" />,
      title: contactInfo?.alternateWebsite ? "Website" : "WhatsApp",
      info: contactInfo?.alternateWebsite
        ? [contactInfo.website, contactInfo.alternateWebsite]
        : ["Send us a message", "Quick response guaranteed"],
      action: contactInfo?.alternateWebsite
        ? contactInfo.website
        : `https://wa.me/${contactInfo?.phoneNumbers?.[0]?.replace(/[^0-9]/g, '') || "918080779602"}`
    }
  ];

  const inputClass = "w-full px-4 py-2.5 border border-slate-300 rounded-lg text-gray-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-150";

  return (
    <section id="contact" ref={ref} className="py-20 bg-white">
      <div className="container mx-auto px-4">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-section font-bold font-heading text-gray-900">Contact Us</h2>
          <div className="mt-3 mx-auto w-12 h-0.5 bg-primary-700 rounded-full" />
          <p className="mt-5 text-lg text-gray-600 max-w-xl mx-auto">
            Have questions about admissions or need personalized guidance? Our team is here to help you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          {/* Contact Cards */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {contactCards.map((item, index) => (
              <a
                key={index}
                href={item.action}
                target={item.action.startsWith('http') ? "_blank" : "_self"}
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-primary-200 hover:bg-primary-50 transition-colors duration-200 group"
              >
                <div className="p-2.5 rounded-lg bg-primary-800 text-white shrink-0 group-hover:bg-primary-900 transition-colors duration-200">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 font-heading">{item.title}</h3>
                  {item.info.map((line, i) => (
                    <p key={i} className="text-sm text-gray-600 mt-0.5">{line}</p>
                  ))}
                </div>
              </a>
            ))}
          </motion.div>

          {/* Form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.18 }}
          >
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-7">
              <h3 className="text-xl font-semibold font-heading text-gray-900 mb-6">Send Us a Message</h3>

              {submitSuccess && (
                <div className="mb-5 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder="your.email@example.com" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="Your phone number" />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                    <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required className={inputClass}>
                      <option value="">Select a subject</option>
                      <option value="MBBS Admission">MBBS Admission</option>
                      <option value="BAMS Admission">BAMS Admission</option>
                      <option value="Engineering Admission">Engineering Admission</option>
                      <option value="Study Abroad">Study Abroad</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                  <textarea id="message" name="message" rows="4" value={formData.message} onChange={handleChange} required className={inputClass} placeholder="How can we help you?" />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-6 bg-primary-800 text-white font-medium rounded-lg hover:bg-primary-900 transition-colors duration-200 flex justify-center items-center gap-2 disabled:opacity-60"
                >
                  {isSubmitting && (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {isSubmitting ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 rounded-xl border border-slate-200 overflow-hidden shadow-sm"
        >
          <div className="h-72">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.2686619580586!2d77.41957611508564!3d28.617929982424736!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cee27e7dc2bd1%3A0xd6b16442e0c9b86e!2s${encodeURIComponent(formattedAddress)}!5e0!3m2!1sen!2sin!4v1632647396223!5m2!1sen!2sin`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Office Location — Vaishnavi Educare, Ghaziabad"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
